import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Checkbox,
  Button,
  Row,
  Col,
  Alert,
  Spin,
  Upload,
  Tag,
  Tooltip,
  message,
  Tabs,
} from "antd";
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  GlobalOutlined,
  ShopOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ProjectOutlined,
  BookOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  PlusOutlined,
  StarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import PagesHeader from "../../components/PageHeader";
import ButtonBar from "../../components/ButtonNavigation";
import {
  getCleaningRestorationProfileData,
  updateCleaningRestorationProfile,
} from "../../services/CleaningRestorationService";
import { getUserData } from "../../services/GetUserData";
import { set_user_auth } from "../../store/actions/user";
import { MEDIA_UPLOAD_URL, WEBSITE_URL } from "../../Helper";
import "./CleaningRestorationDashboard.css";

const { Option } = Select;
const { TabPane } = Tabs;

export const CLEANING_RESTORATION_SERVICES_LIST = [
  { label: "Carpet Cleaning", value: "carpet_cleaning" },
  { label: "Upholstery Cleaning", value: "upholstery_cleaning" },
  { label: "Area Rug Cleaning", value: "area_rug_cleaning" },
  { label: "Tile & Grout Cleaning", value: "tile_grout_cleaning" },
  { label: "Hard Surface Floor Cleaning", value: "hard_surface_floor_cleaning" },
  { label: "Stone Cleaning / Restoration", value: "stone_cleaning_restoration" },
  { label: "Commercial Floor Care", value: "commercial_floor_care" },
  { label: "Carpet Repair", value: "carpet_repair" },
  { label: "Water Damage Restoration", value: "water_damage_restoration" },
  { label: "Structural Drying", value: "structural_drying" },
  { label: "Fire & Smoke Restoration", value: "fire_smoke_restoration" },
  { label: "Mold Remediation", value: "mold_remediation" },
  { label: "Odor Control", value: "odor_control" },
  { label: "Contents Cleaning / Restoration", value: "contents_cleaning_restoration" },
  { label: "Biohazard / Trauma Cleaning", value: "biohazard_trauma_cleaning" },
  { label: "Air Duct / HVAC Cleaning", value: "air_duct_hvac_cleaning" },
];

export const SERVICE_RADIUS_OPTIONS = [
  { label: "25 Miles", value: "25_miles" },
  { label: "50 Miles", value: "50_miles" },
  { label: "100 Miles", value: "100_miles" },
  { label: "200 Miles", value: "200_miles" },
  { label: "350 Miles", value: "350_miles" },
  { label: "500 Miles", value: "500_miles" },
  { label: "Nationwide", value: "nationwide" },
];

const CleaningRestorationDashboard = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.User?.userDetails || state.user?.userData);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [profilePicId, setProfilePicId] = useState(0);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    dispatch(getUserData())
      .then((userData) => {
        if (userData) {
          dispatch(set_user_auth(userData));
        }
        const queryParams = new URLSearchParams(window.location.search);
        const pIdParam = queryParams.get("profile_id") || queryParams.get("cleaning_id") || queryParams.get("id");
        const pId = pIdParam
          ? parseInt(pIdParam, 10)
          : (userData?.cleaning_profile_id || (userData?.profiles && userData?.profiles[0]?.ID) || 0);
        fetchProfile(pId);
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        const queryParams = new URLSearchParams(window.location.search);
        const pIdParam = queryParams.get("profile_id") || queryParams.get("cleaning_id") || queryParams.get("id");
        fetchProfile(pIdParam ? parseInt(pIdParam, 10) : 0);
      });
  }, [location.search]);

  const fetchProfile = (targetId = 0) => {
    setLoading(true);
    dispatch(getCleaningRestorationProfileData(targetId))
      .then((res) => {
        const profileId = res?.profile_id || res?.id;
        if (res && (profileId || res.success)) {
          setProfileData(res);
          const pic = res.profile_pic || res.logo_url || "";
          setImageUrl(pic);
          setProfilePicId(res.profile_pic_id || res.thumbnail_id || 0);

          let servicesVal = [];
          if (Array.isArray(res.cleaning_restoration_services)) {
            servicesVal = res.cleaning_restoration_services;
          } else if (typeof res.cleaning_restoration_services === "string" && res.cleaning_restoration_services.length > 0) {
            servicesVal = res.cleaning_restoration_services.split(",").map((s) => s.trim()).filter(Boolean);
          }

          form.setFieldsValue({
            organization_name: res.organization_name || res.title || "",
            name: res.name || res.first_name || "",
            phone_number: res.phone_number || "",
            email: res.email || "",
            address: res.address || "",
            website_url: res.website_url || "",
            years_in_industry: res.years_in_industry || "",
            service_radius: res.service_radius || "50_miles",
            cleaning_restoration_services: servicesVal,
          });
        }
      })
      .catch((err) => {
        console.error("Error loading Cleaning & Restoration profile:", err);
        message.error("Failed to load Cleaning & Restoration profile data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const beforeUploadImage = (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    if (!isJpgOrPng) {
      message.error("Please upload a JPG, PNG, or WEBP image file.");
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image file size must be less than 5MB.");
      return false;
    }
    return true;
  };

  const handleAvatarUpload = async (file) => {
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", file.name);

    try {
      const res = await axios.post(MEDIA_UPLOAD_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      });
      const resp = res.data;
      const attId = typeof resp === "object" && resp !== null ? resp.id : parseInt(resp, 10);
      const url =
        typeof resp === "object" && resp !== null && (resp.source_url || resp.url)
          ? resp.source_url || resp.url
          : URL.createObjectURL(file);

      if (attId) {
        setProfilePicId(attId);
        setImageUrl(url);
        message.success("Profile photo uploaded successfully!");
      }
    } catch (err) {
      console.error("Avatar upload failed:", err);
      message.error("Failed to upload profile photo.");
    } finally {
      setUploadingAvatar(false);
    }

    return false;
  };

  const handleFinish = (values) => {
    setSaving(true);
    const queryParams = new URLSearchParams(window.location.search);
    const pIdParam = queryParams.get("profile_id") || queryParams.get("cleaning_id") || queryParams.get("id");
    const activeProfileId =
      profileData?.id ||
      profileData?.profile_id ||
      (pIdParam ? parseInt(pIdParam, 10) : currentUser?.cleaning_profile_id || 0);

    const payload = {
      ...values,
      profile_id: activeProfileId,
      id: activeProfileId,
      profile_pic_id: profilePicId || 0,
      profile_pic: imageUrl || "",
    };

    dispatch(updateCleaningRestorationProfile(payload))
      .then((res) => {
        const pId = res?.id || res?.profile_id;
        if (res && (pId || res.success)) {
          setProfileData(res);
          if (res.profile_pic) setImageUrl(res.profile_pic);
          message.success("Cleaning & Restoration profile updated successfully!");
          dispatch(getUserData())
            .then((userData) => {
              if (userData) dispatch(set_user_auth(userData));
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        console.error("Save error:", err);
        message.error("Failed to update profile. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (loading) {
    return (
      <div className="inspectionWrapper" style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading Cleaning & Restoration profile..." />
      </div>
    );
  }

  // Strict boolean check for paid status (handles strings '0', '1', booleans, numbers)
  const checkIsPaid = (val) => {
    if (val === true || val === 1 || val === "1") return true;
    if (typeof val === "string" && val.toLowerCase() === "true") return true;
    return false;
  };

  const isExpired = Boolean(
    currentUser?.expired ||
    profileData?.expired ||
    currentUser?.user_disabled === "1" ||
    currentUser?.user_disabled === 1
  );

  const isPaid =
    !isExpired &&
    (checkIsPaid(currentUser?.is_active_paid) ||
      checkIsPaid(profileData?.is_active_paid) ||
      (Number(currentUser?.membership_id || profileData?.membership_id || 0) === 29 &&
        currentUser?.is_active_paid !== false &&
        currentUser?.is_active_paid !== "0" &&
        currentUser?.is_active_paid !== 0));

  const permalink = profileData?.permalink || "";
  const checkoutUrl = `${WEBSITE_URL}/membership-account/membership-checkout/?level=29`;

  const ProfileuploadButton = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <PlusOutlined style={{ fontSize: 20, color: "#334155", marginBottom: 6 }} />
      <span style={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>
        Upload Profile
      </span>
    </div>
  );

  return (
    <div className="inspectionWrapper trusted-dealer-dashboard-wrapper">
      <div className="inspectionHeader">
        <PagesHeader title="Cleaning & Restoration Profile" />
      </div>

      <div className="buttonSection" style={{ position: "relative", zIndex: 100 }}>
        <div className="container-xxl">
          <ButtonBar isPaid={isPaid} />
        </div>
      </div>

      <div className="container-xxl inspector-profile-container" style={{ padding: "20px 0" }}>
        <Row justify="center">
          <Col xs={24} md={20}>
            {/* Dynamic Status Banners */}
            {!isPaid && (
              <Alert
                message="⚠️ Membership Inactive / Checkout Required"
                description={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 13.5, color: "#7f1d1d", lineHeight: 1.6 }}>
                      Your Cleaning & Restoration membership is currently inactive or cancelled. To activate your public directory listing, unlock{" "}
                      <a
                        href={`${WEBSITE_URL}/defects/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontWeight: 700,
                          textDecoration: "underline",
                          color: "#0a497b",
                        }}
                      >
                        Claims & Conditions
                      </a>
                      , and use your Mobile Photo App seat, please complete checkout.
                    </span>
                    <Button
                      type="primary"
                      danger
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ borderRadius: 6, fontWeight: 700, height: 38 }}
                    >
                      Complete Plan Checkout &rarr;
                    </Button>
                  </div>
                }
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined style={{ fontSize: 24 }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16, background: "#fff5f5", borderColor: "#fecaca" }}
              />
            )}

            {isPaid && (
              <Alert
                message="🟢 Cleaning & Restoration Membership Active"
                description={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 13.5, color: "#065f46" }}>
                      Your membership is active with 1 Mobile FD Photo App Seat included. Your company listing is live on the public directory.
                    </span>
                  </div>
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined style={{ fontSize: 24, color: "#10b981" }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16, background: "#f0fdf4", borderColor: "#bbf7d0" }}
              />
            )}

            {/* EXACT INSPECTOR / CONSULTANT PROFILE FORM ARCHITECTURE */}
            <Form
              form={form}
              id="editProfileForm"
              name="editProfileForm"
              layout="vertical"
              onFinish={handleFinish}
            >
              {/* TOP ROW: AVATAR (LEFT) & STATUS BADGES (RIGHT) */}
              <Row gutter={16} align="middle" justify="space-between">
                <Col xs={24} sm={10} md={8}>
                  <div className="avatar-wrapper">
                    <Spin spinning={uploadingAvatar}>
                      <Upload
                        className="profile-image-upload"
                        showUploadList={false}
                        beforeUpload={beforeUploadImage}
                        customRequest={({ file }) => handleAvatarUpload(file)}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="avatar"
                            className="profileImage-Box"
                            style={{ width: "100%" }}
                          />
                        ) : (
                          ProfileuploadButton
                        )}

                        <Tooltip title="Upload Profile Picture">
                          <div className="upload-button">
                            <CloudUploadOutlined />
                          </div>
                        </Tooltip>
                      </Upload>
                    </Spin>
                  </div>
                </Col>

                {/* STATUS BADGES: PAID / UNPAID */}
                <Col xs={24} sm={14} md={16}>
                  <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 20
                  }}>
                    {isPaid ? (
                      <Tag
                        color="success"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 6px rgba(34, 197, 94, 0.2)",
                          margin: 0
                        }}
                      >
                        <CheckCircleFilled style={{ color: "#16a34a" }} />
                        Paid Plan Active
                      </Tag>
                    ) : (
                      <Tag
                        color="error"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 6px rgba(239, 68, 68, 0.2)",
                          margin: 0
                        }}
                      >
                        <ExclamationCircleOutlined style={{ color: "#dc2626" }} />
                        Unpaid / Checkout Required
                      </Tag>
                    )}

                    {permalink && (
                      <Button
                        type="default"
                        icon={<EyeOutlined />}
                        href={permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ borderRadius: 6, fontWeight: 600 }}
                      >
                        View Public Listing
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {/* BASIC DETAILS (3-COLUMN EXACT INSPECTOR PROFILE GRID) */}
              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Organization / Company Name"
                    name="organization_name"
                    rules={[{ required: true, message: "Please enter organization name!" }]}
                  >
                    <Input
                      prefix={<ShopOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Organization Name"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Contact Person Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter contact name!" }]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Contact Person Name"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Office / Service Address (Map Locator)"
                    name="address"
                    rules={[{ required: true, message: "Please enter business address!" }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Business Address"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ required: true, type: "email", message: "Please enter valid email!" }]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Email"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Phone Number" name="phone_number">
                    <Input
                      prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Phone Number"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Website Link (optional)" name="website_url">
                    <Input
                      prefix={<GlobalOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Website URL"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12} xl={12}>
                  <Form.Item label="Years in Industry (optional)" name="years_in_industry">
                    <Input
                      prefix={<ClockCircleOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="e.g. 15 Years"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} xl={12}>
                  <Form.Item label="Service Radius Coverage" name="service_radius">
                    <Select
                      placeholder="Select service radius"
                      className="service-radius-select"
                      style={{ width: "100%" }}
                    >
                      {SERVICE_RADIUS_OPTIONS.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* SERVICES OFFERED - TABS ARCHITECTURE IDENTICAL TO CONSULTANT */}
              <Tabs defaultActiveKey="1" className="profile-tab-section mt-3">
                <TabPane tab={<span><StarOutlined /> Services Offered</span>} key="1">
                  <div style={{ padding: "16px 0" }}>
                    <div style={{ marginBottom: 16 }}>
                      <span className="tab-content-title" style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                        Cleaning & Restoration Services Offered
                      </span>
                      <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 13 }}>
                        Select the services your company performs. These power search and directory locator filters.
                      </p>
                    </div>

                    <Form.Item name="cleaning_restoration_services">
                      <Checkbox.Group style={{ width: "100%" }}>
                        <Row gutter={[16, 16]}>
                          {CLEANING_RESTORATION_SERVICES_LIST.map((srv) => (
                            <Col xs={24} sm={12} md={8} key={srv.value}>
                              <Checkbox value={srv.value} style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>
                                {srv.label}
                              </Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </div>
                </TabPane>
              </Tabs>

              {/* SAVE BUTTON - IDENTICAL TO CONSULTANT */}
              <div style={{ textAlign: "right", marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  size="large"
                  style={{
                    background: "#0a497b",
                    borderColor: "#0a497b",
                    minWidth: 180,
                    height: 44,
                    borderRadius: 6,
                    fontWeight: 700,
                  }}
                >
                  Save Profile Changes
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CleaningRestorationDashboard;
